/**
 * Desktop Product Detail Page for ISR
 *
 * Performance: Only above-the-fold components (Details, BreadCrumbs, CouponWidget,
 * CrossSell) are statically imported. Below-the-fold sections (reviews, FAQ,
 * suggestions, user guide, video) use dynamic() with ssr:false to reduce
 * initial JS bundle and TBT.
 */

import React, {useEffect, useMemo} from 'react';
import {useAtom} from 'jotai';
import dynamic from 'next/dynamic';
import {ProductDetailSection} from '@/_application/core/types';
import {updateViewedProductInLocalStorage} from '@/_application/core/utils/storage';
import {currentProductDetailIdAtom} from '@/store/product';
import useConversionApi from '@/_application/modules/tracking/meta/tracking-conversion-api';
import {MetaEventList} from '@/_application/modules/tracking/meta/model/meta-conversion';
import {ProductDetailPageProps} from '@/_application/modules/products/pages/product-detail.types';
import {Customer} from '@/__generated__/__types__';

// ============================================
// ABOVE-THE-FOLD: Static imports (critical for LCP)
// ============================================
import Details from '@/_application/modules/products/details/details';
import ProductBreadCrumbs from '@/_application/modules/products/components/breadcrumbs/product-bread-crumbs';
import ProductCrossSell from '@/_application/modules/products/details/product-cross-sell.desktop';
import {CouponWidgetContent} from '@/nova/modules/sales-rule';

// ============================================
// BELOW-THE-FOLD: Dynamic imports (reduces initial JS bundle ~50%)
// ============================================
const ProductDetailNavigation = dynamic(
    () => import('@/_application/modules/products/details/product-detail-navigation'),
    {ssr: false},
);
const ProductAdditionalDataAttribute = dynamic(
    () => import('@/_application/modules/products/details/product-additional-data-attribute'),
    {ssr: false},
);
const ProductUserGuide = dynamic(() => import('@/_application/modules/user-guide/product-user-guide'), {ssr: false});
const ProductTags = dynamic(() => import('@/_application/modules/products/details/product-tags'), {ssr: false});
const ProductReviewsDesktop = dynamic(
    () => import('@/_application/modules/products/details/product-reviews/product-reviews.desktop'),
    {ssr: false},
);
const ProductFAQ = dynamic(() => import('@/_application/modules/faq/components/product-faq'), {ssr: false});
const ProductSuggestedHashTags = dynamic(
    () => import('@/_application/modules/products/details/product-suggested-hash-tags'),
    {ssr: false},
);
const ProductVideoList = dynamic(
    () => import('@/_application/modules/products/details/product-media/product-video-list'),
    {ssr: false},
);
const UsefulInforProductDetail = dynamic(
    () =>
        import('@/_application/modules/useful-information/useful-info-product-detail.desktop').then(m => ({
            default: m.UsefulInforProductDetail,
        })),
    {ssr: false},
);
const ProductsSuggestionBlock = dynamic(
    () =>
        import(
            '@/_application/modules/products/components/products-suggestion/products-suggestion-block'
        ),
    {ssr: false},
);
const ViewedProducts = dynamic(
    () => import('@/_application/modules/products/components/viewed-products/viewed-products'),
    {ssr: false},
);

export function ProductDetailDesktopIsr({
    product: productItem,
    mediaGallery,
    reviews: _reviews,
    customer,
}: Readonly<ProductDetailPageProps & {customer?: Customer | null}>) {
    const [, setProductId] = useAtom(currentProductDetailIdAtom);
    const {sendProcessedDataToConversionAPI} = useConversionApi();
    const eWalletVoucherFilter = useMemo(
        () => ({
            brand: {eq: String(productItem.brand)},
            partner: {eq: '*'},
        }),
        [productItem.brand],
    );

    useEffect(() => {
        if (productItem.id) {
            setProductId(productItem.id);

            if (customer) {
                sendProcessedDataToConversionAPI(
                    MetaEventList.VIEW_CONTENT,
                    {
                        productName: productItem.name,
                        productId: productItem.id,
                    },
                    customer,
                );
            }
        }

        if (productItem.sku) {
            updateViewedProductInLocalStorage(productItem?.sku);
        }
    }, [productItem, customer, setProductId, sendProcessedDataToConversionAPI]);

    return (
        <div className="flex flex-col items-center justify-center bg-secondary">
            <div className="w-full px-5 pt-5 lg:w-[1024px] xl:w-[1204px] xl:px-0">
                <div className="mb-5 flex items-start">
                    <ProductBreadCrumbs product={productItem} />
                </div>

                <div className="flex flex-col gap-5">
                    <div className="grid grid-cols-12 gap-5">
                        <div className="col-span-12 overflow-hidden rounded-[1.25rem]">
                            <Details product={productItem} mediaGallery={mediaGallery} />
                        </div>
                    </div>

                    {productItem.sku && (
                        <CouponWidgetContent
                            variant="voucher"
                            filter={{product: {eq: productItem.sku}}}
                            isMobile={false}
                        />
                    )}

                    {productItem.sku && (
                        <CouponWidgetContent variant="eWalletVoucher" filter={eWalletVoucherFilter} isMobile={false} />
                    )}

                    <ProductCrossSell mainProduct={productItem} />

                    <div className="flex w-full flex-col gap-5">
                        <div className="grid grid-cols-12 gap-5">
                            <div className="col-span-12 flex w-full flex-col gap-5 overflow-hidden xl:col-span-9">
                                <ProductDetailNavigation productItem={productItem} />
                                <ProductAdditionalDataAttribute product={productItem} />
                                <ProductUserGuide product={productItem} />
                                <ProductTags product={productItem} />
                                <div id={ProductDetailSection.REVIEW}>
                                    <ProductReviewsDesktop product={productItem} />
                                </div>

                                <div id={ProductDetailSection.FAQ}>
                                    <ProductFAQ product={productItem} />
                                </div>

                                {productItem?.sku && <ProductSuggestedHashTags sku={productItem.sku} />}
                            </div>

                            <div className="hidden flex-col gap-5 xl:col-span-3 xl:flex">
                                <ProductVideoList product={productItem} />
                                <UsefulInforProductDetail
                                    viewAllLabel="Xem thêm các bài viết hay"
                                    productId={productItem?.id ?? 0}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full">
                <ProductsSuggestionBlock />
            </div>
            <div className="w-full">
                <ViewedProducts />
            </div>
        </div>
    );
}

export default ProductDetailDesktopIsr;



